import React, { useEffect, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { Modal } from "./common";
import Avatar from "./Avatar";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

export default function DeleteStudentModal({ open, onClose, student, onDone }) {
  const { t, pick } = useLang();
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setConfirmText(""); }
  }, [open]);

  if (!student) return null;
  const hasLogin = Boolean(student.login_enabled && student.username);
  const confirmationTarget = hasLogin ? student.username : pick(student, "name");

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/staff/students/${student.id}`);
      toast.success(t("teacher.student_deleted"));
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("teacher.delete_student")} testId="delete-student-modal">
      {step === 1 ? (
        <div className="space-y-5 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#F43F5E]/15 border border-[#F43F5E]/40 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-[#FB7185]" />
          </div>
          <div className="flex items-center justify-center gap-3">
            <Avatar src={student.avatar} name={pick(student, "name")} size="sm" />
            <p className="text-xl font-black">{pick(student, "name")}</p>
          </div>
          <p data-testid="delete-warning-1" className="text-sm font-bold text-[#FFD9DE] bg-[#F43F5E]/10 border border-[#F43F5E]/30 rounded-2xl p-4 leading-relaxed">
            {t("teacher.delete_warn_1", { name: pick(student, "name") })}
          </p>
          <div className="flex gap-3">
            <button data-testid="delete-cancel-1" onClick={onClose} className="btn-magic btn-ghost flex-1">{t("common.cancel")}</button>
            <button data-testid="delete-continue" onClick={() => setStep(2)}
              className="btn-magic flex-1 !bg-none !bg-[#E11D48] hover:!bg-[#F43F5E]" style={{ background: "linear-gradient(90deg,#BE123C,#F43F5E)", boxShadow: "0 4px 18px rgba(244,63,94,0.45)" }}>
              {t("teacher.continue")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <p data-testid="delete-warning-2" className="text-sm font-bold text-[#FFD9DE] bg-[#F43F5E]/10 border border-[#F43F5E]/30 rounded-2xl p-4 leading-relaxed">
            {hasLogin
              ? t("teacher.delete_warn_2", { username: student.username })
              : t("teacher.delete_warn_profile_2", { name: pick(student, "name") })}
          </p>
          <input data-testid="delete-confirm-input" className="input-magic"
            dir={hasLogin ? "ltr" : "auto"} placeholder={confirmationTarget} value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)} />
          <div className="flex gap-3">
            <button data-testid="delete-cancel-2" onClick={() => setStep(1)} className="btn-magic btn-ghost flex-1">{t("common.back")}</button>
            <button data-testid="delete-final-confirm"
              disabled={busy || confirmText.trim() !== confirmationTarget} onClick={doDelete}
              className="btn-magic flex-1" style={{ background: "linear-gradient(90deg,#BE123C,#F43F5E)", boxShadow: "0 4px 18px rgba(244,63,94,0.45)" }}>
              <Trash2 className="w-4 h-4" /> {busy ? t("common.loading") : t("teacher.delete_student")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
